/**
 * Locked verbatim copy from the DateWise Wingman Implementation Spec.
 * Source of truth. Do not paraphrase, shorten, or improve.
 *
 * Reference: DateWise_Wingman_Implementation_Spec.pdf — Sections 2, 3, 5, 6, 7.
 */

export const ANCHORS = {
  hero_h1:
    "Dating after 35 is weird enough. Don\u2019t do it without a wingman.",
  positioning:
    "DateWise Wingman is the second set of eyes for grown-up dating.",
  patterns_not_minds: "We read patterns, not minds.",
  founder_title: "I wasn\u2019t naive. I was rusty.",
  privacy_lead:
    "Your data stays private. Your dating life stays yours.",
};

export const HERO = {
  h1: ANCHORS.hero_h1,
  subheadline:
    "Paste a profile, a strange message, or a screenshot. Get a private, plain-spoken read on red flags, mixed signals, and hidden subtext\u2014plus safer, smoother replies. Built for grown folks who want to slow down before they trust too fast.",
  primary_cta: "Read the Subtext",
};

// Supporting Sections 1\u20135 (Section 3 of spec) in LOCKED order
export const SUPPORTING = [
  {
    n: 1,
    heading: "We read patterns, not minds.",
    body:
      "By reading behavioral patterns instead of minds, DateWise Wingman turns anxious intuition into calm, actionable clarity.",
  },
  {
    n: 2,
    heading: "A second set of eyes on every message.",
    body:
      "Paste profile text or a conversation screenshot and instantly flag risky behavior and decode mixed signals.",
  },
  {
    n: 3,
    heading: "Get a better read on what to say next.",
    body:
      "Draft natural, more confident replies in three styles: Bold & Direct, Playful & Witty, Grounded & Calm.",
  },
  {
    n: 4,
    heading: ANCHORS.privacy_lead,
    body: null, // privacy Version B is rendered here from PRIVACY.B
  },
  {
    n: 5,
    heading: "Built by someone who has been right where you are.",
    body: ANCHORS.founder_title,
  },
];

// Three locked privacy versions (Section 6)
export const PRIVACY = {
  A: "Private and temporary. Your text and screenshots are processed privately and not stored long-term. We do not store your chat histories or sell your identity.",
  B: "Trust is our entire business. DateWise Wingman processes your text and screenshots privately without long-term storage. Your uploads are kept only as long as needed to deliver your read and restore paid access. We don\u2019t track who you are talking to, and we don\u2019t save your personal interactions.",
  C: "Sharing your dating conversations takes trust. We treat your data exactly like a close friend would\u2014we look over your shoulder to give you advice, and then we close the book. Your data is handled privately and kept only as long as required to generate your analysis. You get your clarity, and your personal details stay your own.",
};

// Three paywall versions (Section 5). Default modal shows Version C as the
// lead pitch and renders Version A/B body text inline with each tier card.
export const PAYWALL = {
  A: {
    body:
      "Unlock the Full Read. Get an explicit breakdown of conversational subtext, pattern risk levels, and custom reply drafts in three natural styles.",
    button: "Get the Full Read \u2014 $4.99",
  },
  B: {
    body:
      "Don\u2019t leave your intuition guessing. For less than the cost of most first dates, get a second set of eyes on this profile before you invest your time, energy, and hope.",
    button: "Start My 14-Day Pass \u2014 $14.99",
  },
  C: {
    body:
      "Heading into a weekend of matching? Protect your peace of mind. Activate our 14-day dating pass or monthly access to safely scan as many chats, profiles, and screenshots as you need.",
    buttons: {
      pass: "Start My 14-Day Pass \u2014 $14.99",
      monthly: "Start My Monthly Membership \u2014 $24.99",
    },
  },
};

// Founder story (Section 7) \u2014 polished, verbatim
export const FOUNDER_STORY = {
  title: ANCHORS.founder_title,
  paragraphs: [
    "Twenty years ago, if you wanted to date someone, you met through a coworker, spoke on a landline, or met up for dinner. There were no profile optimization scripts, no unwritten app codes, and no digital tricks designed to move you onto hidden messaging apps before your first real conversation.",
    "When my 19-year marriage ended and I entered my 50s, I downloaded a dating app. Within a few weeks, I realized the world had completely changed. I wasn\u2019t a naive person\u2014I had built a career, raised a family, and lived real life. But I was rusty.",
    "I watched smart, busy, successful friends\u2014adults who were genuinely open to finding love again\u2014get caught up in strange cycles of emotional manipulation, ghosting, catfish accounts, and clear scam patterns. They weren\u2019t dumb. They just didn\u2019t know the new patterns.",
    "I built DateWise Wingman because grown folks shouldn\u2019t have to guess if a text message is an actual human connection or an orchestrated script. We don\u2019t read minds, and we don\u2019t run police background checks. We just read conversational patterns. It\u2019s the exact tool I wish I had in my pocket when I stepped back out into the world: a private, quiet second set of eyes to help you look at the subtext, notice when to slow down, when to lean in, and when to walk away.",
  ],
};

// Verdict labels (locked across product UI)
export const VERDICT_LABELS = {
  green: "LOOKS CLEAN",
  yellow: "EYES OPEN",
  red: "WALK AWAY",
};

// Reply style labels (locked across product UI)
export const REPLY_STYLES = ["Bold & Direct", "Playful & Witty", "Grounded & Calm"];

// Closing CTA band (Section 1, Block 7 of homepage plan)
export const CLOSING_CTA = {
  label: HERO.primary_cta, // "Read the Subtext"
};
